import {
	IChatCommandMaximizeChatMessage,
	IChatCommandMessage,
	IChatCommandMinimizeChatMessage,
	IChatCommandWebMessage,
	IChatCommandSetLangMessage,
	IChatCommandShowTypingMessage,
	IChatCommandSurveyMessage,
	IChatCommandSurveyShowData,
	IChatCommandSurveyStatusData,
	IChatCommandWidgetAccordionData,
	IChatCommandWidgetCarouselData,
	IChatCommandWidgetFormData,
	IChatCommandWidgetHtmlData,
	IChatCommandWidgetMessage,
	IChatCommandWidgetIframeData,
} from '../../types/chat/command';
import { IChatMessages } from '../../types/chat/IChatMessage';
import { CommandType, MessageType } from '../../types/enums';
import { WidgetType } from '../../types/messages/commands';

export const isCommandMessage = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandMessage => message?._type === MessageType.COMMAND;

export const isWidgetCommandMessage = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWidgetMessage =>
	isCommandMessage(message) && message._data.name === CommandType.SHOW_WIDGET;

export const isCommandMessageSurvey = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandSurveyMessage<IChatCommandSurveyShowData> =>
	isCommandMessage(message) && message._data.name === CommandType.SURVEY_SHOW;

export const isCommandMessageSurveyStatus = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandSurveyMessage<IChatCommandSurveyStatusData> =>
	isCommandMessage(message) &&
	(message._data.name === CommandType.SURVEY_ENABLE ||
		message._data.name === CommandType.SURVEY_DISABLE);

export const isCommandMessageSurveyInline = <T extends IChatMessages>(
	message?: T,
): message is T &
	IChatCommandSurveyMessage<
		IChatCommandSurveyShowData & { parameters: { inline: true } }
	> =>
	isCommandMessageSurvey(message) && message._data.parameters?.inline === true;

export const isCommandMessageCarousel = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWidgetMessage<IChatCommandWidgetCarouselData> =>
	isWidgetCommandMessage(message) &&
	message._data.parameters.widget === WidgetType.CAROUSEL;

export const isCommandMessageForm = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWidgetMessage<IChatCommandWidgetFormData> =>
	isWidgetCommandMessage(message) &&
	message._data.parameters.widget === WidgetType.FORM;

export const isCommandMessageFormFullscreen = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWidgetMessage<IChatCommandWidgetFormData> =>
	isCommandMessageForm(message) && !!message._data.parameters.data.fullscreen;

export const isCommandMessageHtml = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWidgetMessage<IChatCommandWidgetHtmlData> =>
	isWidgetCommandMessage(message) &&
	message?._data.parameters.widget === WidgetType.HTML;

export const isCommandMessageIframe = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWidgetMessage<IChatCommandWidgetIframeData> =>
	isWidgetCommandMessage(message) &&
	message?._data.parameters.widget === WidgetType.IFRAME &&
	('url' in message._data.parameters.data ||
		'html' in message._data.parameters.data);

export const isCommandMessageFormLocked = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWidgetMessage<IChatCommandWidgetFormData> =>
	isCommandMessageForm(message) && !!message._data.parameters.data.lock_input;

export const isCommandMessageAccordion = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWidgetMessage<IChatCommandWidgetAccordionData> =>
	isWidgetCommandMessage(message) &&
	message._data.parameters.widget === WidgetType.ACCORDION;

export const isCommandMessageShowTyping = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandShowTypingMessage =>
	isCommandMessage(message) && message._data.name === CommandType.SHOW_TYPING;

export const isCommandMessageMinimizeChat = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandMinimizeChatMessage =>
	isCommandMessage(message) && message._data.name === CommandType.MINIMIZE_CHAT;

export const isCommandMessageMaximizeChat = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandMaximizeChatMessage =>
	isCommandMessage(message) && message._data.name === CommandType.MAXIMIZE_CHAT;

export const isCommandMessageMINMAXChat = <T extends IChatMessages>(
	message?: T,
): message is T &
	(IChatCommandMinimizeChatMessage | IChatCommandMaximizeChatMessage) =>
	isCommandMessageMinimizeChat(message) ||
	isCommandMessageMaximizeChat(message);

export const isCommandWebMessage = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandWebMessage =>
	isCommandMessage(message) && message._data.name === CommandType.WEB_MESSAGE;

export const isCommandSetLang = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandSetLangMessage =>
	isCommandMessage(message) && message._data.name === CommandType.SET_LANG;

export const isCommandCloseChannel = <T extends IChatMessages>(
	message?: T,
): message is T & IChatCommandMessage =>
	isCommandMessage(message) && message._data.name === CommandType.CLOSE_CHANNEL;
